import { toast, Bounce } from 'react-toastify';
import { logout } from '../authMiddleware.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';

let walletConnectionCache = {
  isConnecting: false,
  lastRequestTime: 0,
  cachedAccount: null,
  cacheExpiry: 30000,
  requestDebounceTime: 2000
};

export async function connectWallet(isSignin = false) {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  try {
    const accounts = await window.ncogProvider.request({ method: "ncog_accounts" });
    if (accounts?.selectedAccount?.accountAddress) {
      if (isSignin) {
        const message = "hi there (from Web3)! \n\nTo ensure your ownership of this address, we need you to sign this message. \n\nThis request WILL NOT trigger a blockchain transaction or cost you any gas fees.";
        await window.ncogProvider.request({ method: "ncog_personalSign", params: { userMessage: message } });
      };
      return accounts?.selectedAccount?.accountAddress || null;

    } else {
      return null;
    }
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Wallet-helper.js, Fun: connectWallet, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.log(error)
    return null;
  }
}

// The NCOG extension raises a connect prompt for EVERY ncog_accounts request, so helpers that each
// query independently produce a string of popups. Share one in-flight/resolved request between the
// proof-of-possession helpers below, and drop it when the wallet reports an account change so the next
// read re-queries (a stale publicKey would be signed against the wrong account).
let walletAccountsPromise = null;
let walletAccountsListenerBound = false;

async function fetchWalletAccounts() {
  if (!window?.ncogProvider) return null;
  if (!walletAccountsListenerBound) {
    walletAccountsListenerBound = true;
    try {
      window.ncogProvider.on('accountsChanged', () => { walletAccountsPromise = null; });
    } catch (error) {
      // Provider without event support: the cache is then only valid for this page load, which is fine.
      console.log(error);
    }
  }
  if (!walletAccountsPromise) {
    walletAccountsPromise = window.ncogProvider
      .request({ method: "ncog_accounts" })
      .catch((error) => { walletAccountsPromise = null; throw error; });
  }
  return walletAccountsPromise;
}

export function resetWalletAccountsCache() {
  walletAccountsPromise = null;
}

// The wallet's ML-DSA-87 "asset" signing public key (hex). Sent to /login as signerPublicKey so the
// backend can verify the login challenge signature (the ML-KEM data key this app already sends as
// publicKey cannot sign) and bind it to the account's registered asset address. The deployed wallet's
// ncog_personalSign returns only { signature }, so the public key is sourced here from ncog_accounts.
export async function getWalletPublicKey() {
  try {
    if (!window?.ncogProvider) return null;
    const accounts = await fetchWalletAccounts();
    return accounts?.selectedAccount?.publicKey || null;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Wallet-helper.js, Fun: getWalletPublicKey, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
      level: 'warn',
    });
    console.log(error);
    return null;
  }
}

// The wallet's ASSET (ML-DSA) EVM address = keccak256(mldsaPubKey)[12:]. Recorded on-chain at signup as
// the account's "assetAddress" ATTRIBUTE — the value backend proof-of-possession binds a login signature
// to. It must NEVER be written to USER.walletAddress: that field holds the ML-KEM data address by which
// every DSuite app looks the account up.
export async function getAssetWalletAddress() {
  try {
    if (!window?.ncogProvider) return null;
    const accounts = await fetchWalletAccounts();
    const selected = accounts?.selectedAccount;
    return selected?.walletAddress || selected?.activeWalletAddress || null;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Wallet-helper.js, Fun: getAssetWalletAddress, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
      level: 'warn',
    });
    console.log(error);
    return null;
  }
}

// Sign an arbitrary message (the server login challenge) with the wallet's ML-DSA key.
// The deployed wallet answers { response: { signature } }.
export async function getSignature(message) {
  try {
    if (!window?.ncogProvider) return null;
    const signatureData = await window.ncogProvider.request({
      method: "ncog_personalSign",
      params: { userMessage: message },
    });
    return signatureData?.response?.signature || null;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Wallet-helper.js, Fun: getSignature, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
      level: 'warn',
    });
    console.log(error);
    return null;
  }
}

export async function getCachedWalletAccount() {
  const now = Date.now();

  if (walletConnectionCache.cachedAccount &&
    (now - walletConnectionCache.lastRequestTime) < walletConnectionCache.cacheExpiry) {
    return walletConnectionCache.cachedAccount;
  }

  if (walletConnectionCache.isConnecting) {
    let attempts = 0;
    while (walletConnectionCache.isConnecting && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    return walletConnectionCache.cachedAccount;
  }

  if ((now - walletConnectionCache.lastRequestTime) < walletConnectionCache.requestDebounceTime) {
    return walletConnectionCache.cachedAccount;
  }

  walletConnectionCache.isConnecting = true;
  walletConnectionCache.lastRequestTime = now;

  try {
    if (!window.ncogProvider) {
      walletConnectionCache.cachedAccount = null;
      return null;
    }
    const walletAddress = localStorage.getItem("walletAddress");
    console.log("Retrieved walletAddress from localStorage:", walletAddress);
    if (!walletAddress) {
      walletConnectionCache.cachedAccount = null;
      window.open('/app', '_self');
      return null;
    }
    const accounts = walletAddress

    if (accounts && accounts.error === "Popup closed before connect") {
      toast.error("Please Connect Your NCOG Wallet", {
        toastId: "errorToast",
        position: "top-center",
        transition: Bounce,
      });
      logout();
      walletConnectionCache.cachedAccount = null;
      return null;
    }

    const accountAddress = accounts;
    if (accountAddress) {
      walletConnectionCache.cachedAccount = accountAddress;
      console.log("Wallet connected successfully:", accountAddress);

      if (typeof window !== 'undefined') {
        console.log("Dispatching WALLET_RECONNECTED event");
        window.dispatchEvent(new Event('WALLET_RECONNECTED'));
      }
    } else {
      walletConnectionCache.cachedAccount = null;
    }

    return accountAddress;
  } catch (error) {
    console.log("Error getting cached wallet account:", error);
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Wallet-helper.js, Fun: getCachedWalletAccount, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    if (error && typeof error === 'object' && error.error === 'Popup closed before connect') {
      console.log("Wallet popup was closed by user. Logging out...");

      walletConnectionCache.cachedAccount = null;
      walletConnectionCache.lastRequestTime = 0;

      if (typeof window !== 'undefined' && window.toast) {
        window.toast.error("Please connect to your wallet to continue", {
          position: "top-center",
          autoClose: 3000,
        });
      }

      setTimeout(() => {
        if (typeof window !== 'undefined' && window.location) {
          const currentPath = window.location.pathname;
          if (currentPath.includes('/inbox') || currentPath.includes('/sent') ||
            currentPath.includes('/draft') || currentPath.includes('/settings')) {
            toast.error("Please Connect Your NCOG Wallet", {
              toastId: "errorToast",
              position: "top-center",
              transition: Bounce,
            });
            logout()
            window.location.href = '/app';
          }
        }
      }, 1000);
    }

    walletConnectionCache.cachedAccount = null;
    return null;
  } finally {
    walletConnectionCache.isConnecting = false;
  }
}


export function clearWalletCache() {
  walletConnectionCache = {
    isConnecting: false,
    lastRequestTime: 0,
    cachedAccount: null,
    cacheExpiry: 30000,
    requestDebounceTime: 2000
  };
}

export function forceRefreshWalletCache() {
  walletConnectionCache.lastRequestTime = 0;
  walletConnectionCache.cachedAccount = null;
}
